
import React, { useState } from "react";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import ClientForm from "@/components/clients/ClientForm";
import { PlusCircle, Search, Eye, Edit, Archive } from "lucide-react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import { getClients, archiveClient } from "@/services/ClientService";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function Clients() {
  const [searchTerm, setSearchTerm] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [clientToArchive, setClientToArchive] = useState<string | null>(null);

  const { 
    data: clientsResult, 
    isLoading, 
    refetch 
  } = useQuery({
    queryKey: ['clients', showArchived],
    queryFn: () => getClients(showArchived),
  });

  const clients = clientsResult?.data || [];

  const filteredClients = clients.filter(client => 
    client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.document.includes(searchTerm) ||
    (client.email && client.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleArchiveDialogOpen = (clientId: string) => {
    setClientToArchive(clientId);
    setArchiveDialogOpen(true);
  };

  const handleArchiveClient = async () => {
    if (clientToArchive) {
      await archiveClient(clientToArchive);
      refetch();
      setArchiveDialogOpen(false);
      setClientToArchive(null);
    }
  };

  return (
    <DashboardLayout title="Clientes" isLoading={isLoading}>
      <div className="space-y-6">
        <div className="flex items-center justify-end w-full mb-4">
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="flex items-center gap-2 bg-[#072a45] hover:bg-[#072a45] dark:bg-[#d4af37] dark:hover:bg-[#d4af37] text-white dark:text-[#072a45]">
                <PlusCircle className="h-4 w-4" />
                Novo Cliente
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[800px]  dark:text-gray-700 dark:bg-white">
              <DialogHeader className="px-3">
                <DialogTitle className="text-lg font-semibold">Cadastrar Novo Cliente</DialogTitle>
                <DialogDescription className="text-sm text-muted-foreground">
                  Preencha as informações do cliente abaixo. Todos os campos marcados com * são obrigatórios.
                </DialogDescription>
              </DialogHeader>
              
              <ClientForm 
                onSuccess={() => {
                  setIsDialogOpen(false);
                  refetch();
                }} 
              />
            </DialogContent>
          </Dialog>
        </div>

        
       <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        {/* Campo de busca com ícone */}
        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <Input 
            placeholder="Buscar por nome, CPF/CNPJ ou e-mail"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 w-full bg-white border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-primary focus:outline-none"
          />
        </div>

        {/* Switch de arquivados */}
        <div className="flex items-center space-x-2">
          <Switch
            id="show-archived"
            checked={showArchived}
            onCheckedChange={setShowArchived}
          />
          <Label htmlFor="show-archived" className="text-sm text-gray-700">
            Mostrar arquivados
          </Label>
        </div>
      </div>

        <div className="rounded-md border dark:bg-white">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Documento</TableHead>
                <TableHead>Contato</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton className="h-4 w-[200px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[80px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[120px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[150px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[80px]" /></TableCell>
                  </TableRow>
                ))
              ) : filteredClients && filteredClients.length > 0 ? (
                filteredClients.map(client => (
                  <TableRow key={client.id} className={client.archived ? "opacity-60 bg-gray-50 dark:bg-gray-800/50 dark:text-white" : " dark:text-gray-600"}>
                    <TableCell className="font-medium">
                      {client.name}
                      {client.archived && (
                        <span className="ml-2 text-xs text-gray-500">(Arquivado)</span>
                      )}
                    </TableCell>
                    <TableCell>{client.document_type === 'cpf' ? 'Pessoa Física' : 'Pessoa Jurídica'}</TableCell>
                    <TableCell>{client.document}</TableCell>
                    <TableCell>
                      <div>{client.email || "—"}</div>
                      <div className="text-sm text-gray-500">{client.phone || "—"}</div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" asChild>
                          <Link to={`/dashboard/clients/${client.id}`}>
                            <Eye className="h-4 w-4" />
                            <span className="sr-only">Ver detalhes</span>
                          </Link>
                        </Button>
                        <Button variant="outline" size="sm" asChild>
                          <Link to={`/dashboard/clients/${client.id}?edit=true`}>
                            <Edit className="h-4 w-4" />
                            <span className="sr-only">Editar</span>
                          </Link>
                        </Button>
                        {!client.archived && (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => handleArchiveDialogOpen(client.id)}
                          >
                            <Archive className="h-4 w-4" />
                            <span className="sr-only">Arquivar</span>
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                    {searchTerm ? 'Nenhum cliente encontrado para a busca.' : 'Nenhum cliente cadastrado.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <AlertDialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Arquivar cliente</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja arquivar este cliente? Clientes arquivados não são exibidos por padrão nas listagens.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleArchiveClient}>
              Arquivar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
